import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ArrowRight, Building2, CheckCircle2, FileText, Landmark, Mail, Menu, Phone, ShieldCheck, X } from 'lucide-react';
import { siteData } from './data/siteData';
import './styles.css';

const emailHref = `mailto:${siteData.contact.email}`;

const navItems = [
  { label: 'About', path: '/about-us' },
  { label: 'Solutions', path: '/solutions' },
  { label: 'Industries', path: '/industries' },
  { label: 'Community', path: '/community' },
  { label: 'Contact', path: '/contact-us' }
];

const iconMap = {
  Government: Landmark,
  Healthcare: ShieldCheck,
  Legal: FileText,
  'Private Schools': Building2
};

const seoOverrides = {
  '/': {
    title: 'Fractional CISO & CMMC Support | Slate Enclave',
    description: 'Slate Enclave provides Fractional CISO services, CMMC support, cybersecurity compliance, assessments, and security advisory services for regulated organizations.'
  },
  '/solutions/ciso-as-a-service': {
    title: 'Fractional CISO Services | Slate Enclave',
    description: 'Fractional CISO services from Slate Enclave help organizations build security programs, manage cyber risk, improve reporting, and align security with business goals.'
  },
  '/solutions/cmmc-dfars': {
    title: 'CMMC Support & DFARS Compliance | Slate Enclave',
    description: 'Slate Enclave provides CMMC support, DFARS readiness, gap assessments, remediation guidance, and cybersecurity compliance support for defense contractors.'
  },
  '/solutions': {
    title: 'Cybersecurity Services, Fractional CISO & CMMC Support | Slate Enclave',
    description: 'Explore Slate Enclave cybersecurity services including Fractional CISO, CMMC support, assessment and authorization, penetration testing, and secure engineering.'
  },
  '/solutions-2': {
    title: 'Cybersecurity Services, Fractional CISO & CMMC Support | Slate Enclave',
    description: 'Explore Slate Enclave cybersecurity services including Fractional CISO, CMMC support, assessment and authorization, penetration testing, and secure engineering.'
  }
};

const marketingDescriptions = {
  '/': 'Fractional CISO leadership and CMMC support for organizations that need practical cybersecurity guidance, compliance readiness, and measurable risk reduction.',
  '/solutions/ciso-as-a-service': 'Fractional CISO services that provide executive security leadership, program development, risk reporting, and compliance guidance without the overhead of a full-time CISO.',
  '/solutions/cmmc-dfars': 'CMMC support and DFARS readiness for defense contractors, including gap assessments, remediation planning, security program alignment, and audit preparation.'
};

const focusCards = [
  {
    title: 'Fractional CISO Leadership',
    path: '/solutions/ciso-as-a-service',
    text: 'Executive-level security guidance, program roadmaps, risk reporting, vendor-agnostic advice, and board-ready communication without adding a full-time CISO.'
  },
  {
    title: 'CMMC Support',
    path: '/solutions/cmmc-dfars',
    text: 'Readiness support for defense contractors, including CMMC gap assessments, DFARS alignment, remediation planning, documentation, and audit preparation.'
  }
];

const imagePositions = {
  '/solutions/pentesting': 'center top',
  '/solutions/assessment-authorization': 'center top'
};

function cleanPath(pathname) {
  return pathname === '/' ? '/' : pathname.replace(/\/$/, '');
}

function findContent(path) {
  const normalized = cleanPath(path);
  return [...siteData.pages].find((item) => item.path === normalized);
}

function internalHref(path) {
  return path || '/';
}

function setMeta(page) {
  const seo = page ? seoOverrides[page.path] : null;
  const title = seo?.title || (page ? `${page.title} | Slate Enclave` : 'Slate Enclave | Fractional CISO & CMMC Support');
  document.title = title;

  const ensure = (selector, create) => {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = create();
      document.head.appendChild(el);
    }
    return el;
  };

  ensure('meta[name="description"]', () => {
    const el = document.createElement('meta');
    el.setAttribute('name', 'description');
    return el;
  }).setAttribute('content', seo?.description || page?.description || 'Slate Enclave provides Fractional CISO, CMMC support, cybersecurity, and compliance solutions.');

  ensure('link[rel="canonical"]', () => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    return el;
  }).setAttribute('href', `https://www.slateenclave.com${page?.path === '/' ? '/' : `${page?.path || ''}/`}`);
}

function Header({ currentPath }) {
  const [open, setOpen] = useState(false);
  const solutions = siteData.pages.filter((page) => page.path.startsWith('/solutions/') && page.path !== '/solutions-2');
  const industries = siteData.pages.filter((page) => page.path.startsWith('/industries/'));

  useEffect(() => setOpen(false), [currentPath]);

  return (
    <header className="site-header">
      <a className="brand" href="/" aria-label="Slate Enclave home">
        <img src="/assets/slate_logo.png" alt="Slate Enclave" />
      </a>
      <button className="menu-button" onClick={() => setOpen(!open)} aria-label="Toggle navigation">
        {open ? <X size={22} /> : <Menu size={22} />}
      </button>
      <nav className={open ? 'nav open' : 'nav'} aria-label="Primary navigation">
        {navItems.map((item) => (
          <div className="nav-group" key={item.path}>
            <a className={currentPath === item.path ? 'active' : ''} href={internalHref(item.path)}>
              {item.label}
            </a>
            {item.label === 'Solutions' && <Dropdown items={solutions} />}
            {item.label === 'Industries' && <Dropdown items={industries} />}
          </div>
        ))}
        <a className="nav-cta" href={emailHref}>
          <Mail size={17} />
          Contact Us
        </a>
      </nav>
    </header>
  );
}

function Dropdown({ items }) {
  return (
    <div className="dropdown">
      {items.map((item) => (
        <a href={item.path} key={item.path}>
          {item.title}
        </a>
      ))}
    </div>
  );
}

function Hero({ page, eyebrow = 'Slate Enclave' }) {
  const description = marketingDescriptions[page.path] || page.description;
  return (
    <section
      className="hero"
      style={{
        '--hero-image': `url("${page.hero || '/assets/slateheaderimage.png'}")`,
        '--hero-position': imagePositions[page.path] || 'center'
      }}
    >
      <div className="hero-inner">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{page.title === 'Home' ? 'Fractional CISO & CMMC Support for Modern Organizations' : page.title}</h1>
        <p>{description}</p>
        <div className="hero-actions">
          <a className="button primary" href={emailHref}>
            <Mail size={18} />
            Contact Us
          </a>
          <a className="button secondary" href="/solutions/cmmc-dfars">
            CMMC Support
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

function HomePage({ page }) {
  const solutions = siteData.pages.filter((item) => item.path.startsWith('/solutions/') && item.blocks.length);
  const industries = siteData.pages.filter((item) => item.path.startsWith('/industries/'));
  const trust = [
    { title: 'Reliable', img: '/assets/reliable.png', text: 'Transparent partnerships built around the security outcomes your organization needs.' },
    { title: 'Experienced', img: '/assets/experienced.png', text: 'More than 20 years supporting federal, commercial, private, and intelligence community environments.' },
    { title: 'Secure', img: '/assets/secure.png', text: 'A holistic approach spanning culture, management, operations, engineering, and technical controls.' }
  ];

  return (
    <>
      <Hero page={page} />
      <section className="intro-band">
        <div>
          <p className="section-kicker">Cybersecurity leadership</p>
          <h2>Fractional CISO services and CMMC support built for real operating constraints.</h2>
        </div>
        <p>Slate helps organizations reduce cyber risk through executive security leadership, CMMC readiness, compliance support, security engineering, penetration testing, and practical advisory services.</p>
      </section>
      <section className="focus-section" aria-label="Primary services">
        {focusCards.map((item) => (
          <a className="focus-card" href={item.path} key={item.title}>
            <CheckCircle2 size={28} />
            <div>
              <h2>{item.title}</h2>
              <p>{item.text}</p>
              <span>
                Learn more <ArrowRight size={16} />
              </span>
            </div>
          </a>
        ))}
      </section>
      <section className="panel-grid trust-grid" aria-label="Slate strengths">
        {trust.map((item) => (
          <article className="feature-card" key={item.title}>
            <img src={item.img} alt="" />
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </section>
      <Listing title="Cybersecurity Services" kicker="Fractional CISO, CMMC, and compliance support" items={solutions} limit={6} />
      <IndustryStrip items={industries} />
      <CtaBand />
    </>
  );
}

function Listing({ title, kicker, items, limit }) {
  const orderedItems = [...items].sort((a, b) => {
    const priority = ['/solutions/ciso-as-a-service', '/solutions/cmmc-dfars'];
    return (priority.indexOf(a.path) === -1 ? 99 : priority.indexOf(a.path)) - (priority.indexOf(b.path) === -1 ? 99 : priority.indexOf(b.path));
  });

  return (
    <section className="section">
      <div className="section-heading">
        <p className="section-kicker">{kicker}</p>
        <h2>{title}</h2>
      </div>
      <div className="card-grid">
        {orderedItems.slice(0, limit || orderedItems.length).map((item) => (
          <a className="content-card" href={item.path} key={item.path}>
            <img src={item.hero} alt="" style={{ objectPosition: imagePositions[item.path] || 'center' }} />
            <div>
              <h3>{item.title}</h3>
              <p>{marketingDescriptions[item.path] || item.description}</p>
              <span>
                Learn more <ArrowRight size={16} />
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function IndustryStrip({ items }) {
  return (
    <section className="industry-strip">
      <div className="section-heading compact">
        <p className="section-kicker">Industry focus</p>
        <h2>Programs shaped for regulated and high-trust environments.</h2>
      </div>
      <div className="industry-grid">
        {items.map((item) => {
          const Icon = iconMap[item.title] || ShieldCheck;
          return (
            <a className="industry-item" href={item.path} key={item.path}>
              <Icon size={24} />
              <span>{item.title}</span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="cta-band">
      <div>
        <p className="section-kicker">Fractional CISO & CMMC support</p>
        <h2>Start with a clear conversation about risk, compliance readiness, and security leadership.</h2>
      </div>
      <a className="button primary" href={emailHref}>
        <Mail size={18} />
        Contact Us
      </a>
    </section>
  );
}

function PageBody({ page }) {
  const relatedSolutions = siteData.pages.filter((item) => item.path.startsWith('/solutions/') && item.path !== page.path);
  const relatedIndustries = siteData.pages.filter((item) => item.path.startsWith('/industries/') && item.path !== page.path);

  if (page.path === '/solutions' || page.path === '/solutions-2') {
    return <Listing title="Solutions" kicker="Capabilities" items={siteData.pages.filter((item) => item.path.startsWith('/solutions/') && item.blocks.length)} />;
  }

  if (page.path === '/industries') {
    return <Listing title="Industries" kicker="Who Slate serves" items={siteData.pages.filter((item) => item.path.startsWith('/industries/'))} />;
  }

  // if (page.path === '/resources-industry-news') {
  //   return <ResourcesPage page={page} />;
  // }

  if (page.path === '/contact-us' || page.path === '/contact') {
    return <ContactPage page={page} />;
  }

  return (
    <>
      <section className="content-layout">
        <article className="prose">
          {page.blocks.map((block, index) => <Block block={block} key={`${block.type}-${index}`} />)}
        </article>
        <aside className="aside">
          <h2>Work with Slate</h2>
          <p>Discuss Fractional CISO support, CMMC readiness, compliance needs, and the security outcomes that matter most.</p>
          <a className="button primary full" href={emailHref}>
            <Mail size={18} />
            Contact Us
          </a>
          <div className="quick-links">
            <h3>Related pages</h3>
            {[...relatedSolutions, ...relatedIndustries].slice(0, 5).map((item) => (
              <a href={item.path} key={item.path}>{item.title}</a>
            ))}
          </div>
        </aside>
      </section>
      <CtaBand />
    </>
  );
}

function Block({ block }) {
  if (block.type === 'heading') return <h2>{block.text}</h2>;
  if (block.type === 'list') {
    return (
      <ul>
        {block.items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    );
  }
  return <p>{block.text}</p>;
}

function GenericPage({ page }) {
  return (
    <>
      <Hero page={page} eyebrow={'Slate Enclave'} />
      <PageBody page={page} />
    </>
  );
}
// function ResourcesPage({ page }) {
//   return (
//     <>
//       <section className="content-layout">
//         <article className="prose">
//           {page.blocks.map((block, index) => <Block block={block} key={`${block.type}-${index}`} />)}
//         </article>
//         <aside className="aside">
//           <h2>Need direct support?</h2>
//           <p>The newsletter form has been replaced with direct contact, per request.</p>
//           <a className="button primary full" href={emailHref}>
//             <Mail size={18} />
//             Contact Us
//           </a>
//         </aside>
//       </section>
//     </>
//   );
// }

function ContactPage({ page }) {
  return (
    <section className="contact-section">
      <div className="contact-card">
        <p className="section-kicker">Contact Slate</p>
        <h2>Start a conversation about Fractional CISO or CMMC support.</h2>
        <p>Use the button below to open your local email client with Slate's address prefilled.</p>
        <a className="button primary" href={emailHref}>
          <Mail size={18} />
          Contact Us
        </a>
      </div>
      <div className="contact-details">
        <h2>Slate Enclave</h2>
        {siteData.contact.address.map((line) => <p key={line}>{line}</p>)}
        <a href={emailHref}><Mail size={18} />{siteData.contact.email}</a>
        <a href="tel:+14109891102"><Phone size={18} />{siteData.contact.phone}</a>
      </div>
    </section>
  );
}

function NotFound() {
  return (
    <main>
      <section className="not-found">
        <h1>Page not found</h1>
        <p>The requested Slate Enclave page is not part of the migrated route set.</p>
        <a className="button primary" href="/">Return home</a>
      </section>
    </main>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <img src="/assets/slate_logo.png" alt="Slate Enclave" />
        <p>Fractional CISO, CMMC support, and custom cybersecurity solutions.</p>
      </div>
      <div>
        <h2>Contact</h2>
        <a href={emailHref}>{siteData.contact.email}</a>
        <a href="tel:+14109891102">{siteData.contact.phone}</a>
      </div>
      <div>
        <h2>Company</h2>
        <a href="/privacy-policy">Privacy Policy</a>
        <a href="/community">Community</a>
      </div>
    </footer>
  );
}

function App() {
  const [path, setPath] = useState(cleanPath(window.location.pathname));
  const page = useMemo(() => findContent(path), [path]);

  useEffect(() => {
    const onPop = () => setPath(cleanPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => setMeta(page), [page]);

  useEffect(() => {
    const handler = (event) => {
      const anchor = event.target.closest('a[href^="/"]');
      if (!anchor || anchor.target || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      window.history.pushState({}, '', anchor.getAttribute('href'));
      setPath(cleanPath(window.location.pathname));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  if (!page) return <><Header currentPath={path} /><NotFound /><Footer /></>;
  return (
    <>
      <Header currentPath={path} />
      <main>
        {page.path === '/' ? <HomePage page={page} /> : <GenericPage page={page} />}
      </main>
      <Footer />
    </>
  );
}

createRoot(document.getElementById('root')).render(<App />);
